import { TimeTrackingHistory } from "@jugl-web/domain-resources/task-time-tracking/components/TimeTrackingHistory";
import {
  TimeTrackingStatusPanel,
  TimeTrackingStatusPanelTypeSpecificProps,
} from "@jugl-web/domain-resources/task-time-tracking/components/TimeTrackingStatusPanel";
import { useActiveTimeTrackingContext } from "@jugl-web/domain-resources/task-time-tracking/providers/ActiveTimeTrackingProvider";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { TaskTimeLogModel } from "@jugl-web/rest-api/tasks/models/TaskTimeLog/TaskTimeLogModel";
import { useMe } from "@web-src/modules/auth/hooks/useMe";
import { FC } from "react";
import {
  TimeTrackedPopoverHeader,
  TimeTrackedPopoverHeaderTypeSpecificProps,
} from "./components/TimeTrackedPopoverHeader";

interface TimeTrackedPopoverContentProps {
  entityId: string;
  taskId: string;
  header: TimeTrackedPopoverHeaderTypeSpecificProps;
  withHistory?: boolean;
  onClose: () => void;
  onAddTimeLog?: () => void;
  onEditTimeLog?: (timeLog: TaskTimeLogModel) => void;
  onDeleteTimeLog?: (timeLog: TaskTimeLogModel) => void;
  onShowAll?: () => void;
}

export const TimeTrackedPopoverContent: FC<TimeTrackedPopoverContentProps> = ({
  entityId,
  taskId,
  header: headerProps,
  withHistory = false,
  onClose,
  onAddTimeLog,
  onEditTimeLog,
  onDeleteTimeLog,
  onShowAll,
}) => {
  const { tasksApi } = useRestApiProvider();

  const { data: task } = tasksApi.useGetTaskQuery({
    entityId,
    taskId,
    meta: { silent: true },
  });

  const { activeTimeLog, startTimer, stopActiveTimer } =
    useActiveTimeTrackingContext();

  const { id: meId = "" } = useMe();

  const isActive = activeTimeLog?.taskId === taskId;

  const statusPanelProps: TimeTrackingStatusPanelTypeSpecificProps = isActive
    ? {
        type: "active",
        timeLogId: activeTimeLog.id,
        startDate: activeTimeLog.startDate,
      }
    : { type: "idle", totalDuration: task?.time_tracked || 0 };

  return (
    <div className="flex flex-col">
      <TimeTrackedPopoverHeader
        onShowAll={() => onShowAll?.()}
        onClose={onClose}
        {...headerProps}
      />
      <div className="px-4 py-6">
        <TimeTrackingStatusPanel
          onStopTimer={stopActiveTimer}
          onStartTimer={() => startTimer(taskId)}
          onAddTimeLog={() => {
            onAddTimeLog?.();
            onClose();
          }}
          {...statusPanelProps}
        />
      </div>
      {withHistory && (
        <>
          <div className="h-px w-full shrink-0 bg-dark-100" />
          <TimeTrackingHistory
            entityId={entityId}
            taskId={taskId}
            meId={meId}
            onShowAll={() => {
              onShowAll?.();
              onClose();
            }}
            onEditTimeLog={(timeLog) => {
              onEditTimeLog?.(timeLog);
              onClose();
            }}
            onDeleteTimeLog={(timeLog) => {
              onDeleteTimeLog?.(timeLog);
              onClose();
            }}
          />
          <div />
        </>
      )}
    </div>
  );
};
